import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { get, post } from "@/lib/api-client";
import { newIdempotencyKey } from "@/lib/idempotency";
import { formatCurrency, formatNumber, formatDateTime } from "@/lib/format";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { Badge } from "@/components/ui/Badge";
import { StatusPill } from "@/components/ui/StatusPill";

export function MembershipPage() {
  const qc = useQueryClient();

  const plans = useQuery({
    queryKey: ["membership-plans"],
    queryFn: () => get<any[]>("/membership/plans"),
  });
  const purchases = useQuery({
    queryKey: ["membership-purchases"],
    queryFn: () => get<any[]>("/membership/purchases/list"),
  });

  const initiate = useMutation({
    mutationFn: (plan_id: number) =>
      post<any>("/membership/purchases", { plan_id }, { idempotencyKey: newIdempotencyKey() }),
    onSuccess: (p) => {
      toast.success(`Package selected! Please submit payment details.`);
      qc.invalidateQueries({ queryKey: ["membership-purchases"] });
      // 🛠️ FIXED: Redirect to payment page!
      window.location.href = `/dashboard/membership/pay/${p.id}`; 
    },
  });

  return (
    <div className="space-y-6 animate-fade-in">
      <header>
        <h1 className="text-2xl font-bold text-surface-900">Buy Points</h1>
        <p className="text-sm text-surface-500 mt-1">
          Purchase points directly. If your total available points reach the Club Entry threshold, entries will be created automatically.
        </p>
      </header>

      <div>
        <div className="text-sm font-semibold text-surface-800 mb-3">Available Point Packages</div>
        {plans.isLoading ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-48 rounded-xl" />)}
          </div>
        ) : plans.data && plans.data.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {plans.data.map((p) => (
              <Card key={p.id} className="relative overflow-hidden border-brand-200">
                <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-brand-500/10 blur-3xl" />
                <CardBody className="relative flex flex-col h-full">
                  <div className="flex justify-between items-start">
                    <Badge tone="info" className="bg-brand-50 text-brand-700">
                      <Sparkles className="h-3 w-3 mr-1" /> Package
                    </Badge>
                  </div>
                  <div className="mt-4 text-3xl font-black text-brand-600 font-mono tracking-tighter">
                    {formatNumber(p.points_awarded)} <span className="text-sm font-bold text-brand-400">PTS</span>
                  </div>
                  <div className="mt-1 text-sm font-bold text-surface-900">{p.name}</div>
                  <div className="mt-1 text-xs text-surface-500 line-clamp-2 flex-1">{p.description || "Direct points purchase."}</div>
                  
                  <div className="mt-5 flex items-center justify-between border-t border-surface-100 pt-4">
                    <span className="text-2xl font-extrabold text-surface-900">{formatCurrency(p.price)}</span>
                    <Button loading={initiate.isPending} onClick={() => initiate.mutate(p.id)}>
                      Buy Now
                    </Button>
                  </div>
                </CardBody>
              </Card>
            ))}
          </div>
        ) : (
          <EmptyState icon={Sparkles} title="No packages available" description="Check back later." />
        )}
      </div>

      <Card>
        <CardHeader><CardTitle>Purchase History</CardTitle></CardHeader>
        <CardBody className="p-0">
          {purchases.isLoading ? (
            <div className="p-5"><Skeleton className="h-12 w-full" /></div>
          ) : purchases.data && purchases.data.length > 0 ? (
            <ul className="divide-y divide-surface-100">
              {purchases.data.map((p) => (
                <li key={p.id} className="flex items-center justify-between px-5 py-4 hover:bg-surface-50 transition-colors">
                  <div>
                    <div className="text-sm font-bold text-surface-900">
                      {p.plan_name} <span className="text-brand-600 font-mono ml-1">+{p.points_awarded} Pts</span>
                    </div>
                    <div className="text-xs font-medium text-surface-500 mt-0.5">
                      #{p.id} • {formatCurrency(p.plan_price)} • {formatDateTime(p.created_at)}
                    </div>
                  </div>
                  <StatusPill status={p.status} />
                </li>
              ))}
            </ul>
          ) : (
            <div className="p-8 text-center text-sm text-surface-500">No purchases yet.</div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}